export interface TimeseriesVisibilityOptions {
    /** When true, keep buckets that only have prior-period activity. */
    includePriorOnlyBuckets: boolean;
}

export interface TimeseriesPointActivity {
    hasCurrentActivity: boolean;
    hasPriorActivity: boolean;
}

export function shouldIncludeTimeseriesPoint(
    activity: TimeseriesPointActivity,
    options: TimeseriesVisibilityOptions,
): boolean {
    if (options.includePriorOnlyBuckets) {
        return activity.hasCurrentActivity || activity.hasPriorActivity;
    }
    return activity.hasCurrentActivity;
}

export function filterTimeseriesPointsForDisplay<T>(
    points: T[],
    options: TimeseriesVisibilityOptions,
    getActivity: (point: T) => TimeseriesPointActivity,
): T[] {
    return points.filter((point) => shouldIncludeTimeseriesPoint(getActivity(point), options));
}

/** Overview / marketplace timeseries: current-period order activity. */
export function getOverviewTimeseriesActivity(point: {
    orderCount?: number;
    orderVolume?: number;
    partsOrdered?: number;
    prior?: { orderCount?: number; orderVolume?: number; partsOrdered?: number };
}): TimeseriesPointActivity {
    const hasCurrentActivity =
        (point.orderCount ?? 0) > 0
        || (point.partsOrdered ?? 0) > 0
        || (point.orderVolume ?? 0) > 0;
    const hasPriorActivity =
        (point.prior?.orderCount ?? 0) > 0
        || (point.prior?.partsOrdered ?? 0) > 0
        || (point.prior?.orderVolume ?? 0) > 0;

    return { hasCurrentActivity, hasPriorActivity };
}

type InvoicingActivityPoint = {
    invoiceCount?: number;
    manuallySubmittedCount?: number;
    prior?: { invoiceCount?: number; manuallySubmittedCount?: number };
};

function hasInvoiceActivity(point?: { invoiceCount?: number }): boolean {
    return (point?.invoiceCount ?? 0) > 0;
}

function hasFlagActivity(point?: { manuallySubmittedCount?: number }): boolean {
    return (point?.manuallySubmittedCount ?? 0) > 0;
}

/** Invoicing timeseries: keep dates with activity for the selected metric. */
export function getInvoicingTimeseriesActivity(
    point: InvoicingActivityPoint,
    metric?: string,
): TimeseriesPointActivity {
    const isActive = (bucket?: InvoicingActivityPoint): boolean => {
        switch (metric) {
            case 'invoiceCount':
            case 'invoiceValue':
            case 'autoSubmissionRate':
                return hasInvoiceActivity(bucket);
            case 'invoiceFlags':
            case 'invoiceFlagsValue':
                return hasFlagActivity(bucket);
            default:
                return hasInvoiceActivity(bucket) || hasFlagActivity(bucket);
        }
    };

    return {
        hasCurrentActivity: isActive(point),
        hasPriorActivity: isActive(point.prior),
    };
}

/** Domain timeseries rows for one date across selected entities. */
export function getDomainDateActivity(
    points: Array<{ orderCount?: number; prior?: { orderCount?: number } }>,
): TimeseriesPointActivity {
    return {
        hasCurrentActivity: points.some((point) => (point.orderCount ?? 0) > 0),
        hasPriorActivity: points.some((point) => (point.prior?.orderCount ?? 0) > 0),
    };
}

export function shouldIncludeDomainTimeseriesDate(
    date: string,
    data: Array<{ date: string; domainId?: string; orderCount?: number; prior?: { orderCount?: number } }>,
    selectedEntityIds: string[],
    options: TimeseriesVisibilityOptions,
): boolean {
    const relevantPoints = data.filter((point) => (
        point.date === date
        && (selectedEntityIds.length === 0 || selectedEntityIds.includes(point.domainId ?? ''))
    ));
    return shouldIncludeTimeseriesPoint(getDomainDateActivity(relevantPoints), options);
}
